import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LuDatabase, LuGlobe, LuTerminal } from "react-icons/lu";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";

type Scope = "product" | "console";

export function DatabasePage() {
  const [scope, setScope] = useState<Scope>("product");
  const [active, setActive] = useState<string | null>(null);

  const tablesQuery = useQuery({
    queryKey: ["console", "database", "tables", scope],
    queryFn: () => api.database.tables(scope),
  });
  const rowsQuery = useQuery({
    queryKey: ["console", "database", "rows", scope, active],
    queryFn: () =>
      active
        ? api.database.rows(active, { limit: 50, scope })
        : Promise.reject(),
    enabled: !!active,
  });

  const tableColumns: Column<{ name: string; rows: number }>[] = [
    { header: "Table", cell: (t) => <code className="text-xs">{t.name}</code> },
    {
      header: "Rows",
      cell: (t) => t.rows.toLocaleString(),
      className: "text-[var(--color-text-muted)] text-right",
    },
  ];

  return (
    <>
      <PageHeader
        icon={LuDatabase}
        title="Database"
        description="Read-only browser. Pick a database, then a table to inspect rows."
      />
      <div className="p-8 space-y-6">
        <ScopeTabs
          scope={scope}
          onChange={(s) => {
            setScope(s);
            setActive(null);
          }}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
          <Card className="self-start">
            <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
                Tables
              </p>
              {tablesQuery.data && (
                <span className="text-[10px] text-[var(--color-text-subtle)]">
                  {tablesQuery.data.tables.length}
                </span>
              )}
            </div>
            <DataTable
              columns={tableColumns}
              rows={tablesQuery.data?.tables}
              isLoading={tablesQuery.isLoading}
              isError={tablesQuery.isError}
              rowKey={(t) => t.name}
              onRowClick={(t) => setActive(t.name)}
            />
          </Card>

          <Card className="overflow-hidden">
            {!active ? (
              <div className="p-12 text-center text-sm text-[var(--color-text-muted)]">
                Pick a table to load.
              </div>
            ) : rowsQuery.isLoading ? (
              <div className="p-12 text-center text-sm text-[var(--color-text-muted)]">
                Loading rows for <code>{active}</code>…
              </div>
            ) : rowsQuery.isError ? (
              <div className="p-12 text-center text-sm text-[var(--color-error)]">
                Couldn't load rows for <code>{active}</code>.
              </div>
            ) : rowsQuery.data ? (
              <>
                <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
                  <p className="text-sm font-semibold text-[var(--color-text)]">
                    <code>{active}</code>
                    <span className="text-[var(--color-text-subtle)] text-xs ml-2">
                      {rowsQuery.data.total.toLocaleString()} total
                    </span>
                  </p>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
                    {scope === "console" ? "console_db" : "product_db"}
                  </span>
                </div>
                <div className="overflow-auto max-h-[70vh]">
                  <table className="w-full text-xs font-mono">
                    <thead className="sticky top-0 bg-[var(--color-surface)]">
                      <tr className="border-b border-[var(--color-border)]">
                        {rowsQuery.data.columns.map((col) => (
                          <th
                            key={col.name}
                            className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[var(--color-text-subtle)] whitespace-nowrap"
                          >
                            {col.name}
                            {col.pk === 1 && (
                              <span className="ml-1 text-[var(--color-accent-light)]">PK</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rowsQuery.data.rows.map((row, i) => (
                        <tr
                          key={i}
                          className="border-b border-[var(--color-border)] hover:bg-[var(--color-elevated)]/30 transition-colors"
                        >
                          {rowsQuery.data.columns.map((col) => (
                            <td
                              key={col.name}
                              className="px-3 py-2 align-top whitespace-nowrap text-[var(--color-text)]"
                            >
                              {renderCell(row[col.name])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </Card>
        </div>
      </div>
    </>
  );
}

function ScopeTabs({
  scope,
  onChange,
}: {
  scope: Scope;
  onChange: (s: Scope) => void;
}) {
  const tabs: {
    id: Scope;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    description: string;
  }[] = [
    {
      id: "product",
      label: "Product DB",
      icon: LuGlobe,
      description:
        "destroy.network primary database: users, inboxes, messages, sponsors, public domains, etc.",
    },
    {
      id: "console",
      label: "Console DB",
      icon: LuTerminal,
      description:
        "Operator console database: console_users, console_audit_log, console_events, console_cron_runs, console_auth_codes.",
    },
  ];
  const active = tabs.find((t) => t.id === scope);
  return (
    <div>
      <div className="inline-flex rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-1">
        {tabs.map((t) => {
          const isActive = t.id === scope;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              className={[
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-[rgba(139,92,246,0.12)] text-[var(--color-text)] border border-[rgba(139,92,246,0.3)]"
                  : "text-[var(--color-text-muted)] hover:text-[var(--color-text)] border border-transparent",
              ].join(" ")}
            >
              <t.icon
                className={`w-3.5 h-3.5 ${
                  isActive
                    ? "text-[var(--color-accent-light)]"
                    : "text-[var(--color-text-subtle)]"
                }`}
              />
              {t.label}
            </button>
          );
        })}
      </div>
      {active && (
        <p className="text-xs text-[var(--color-text-subtle)] mt-2">{active.description}</p>
      )}
    </div>
  );
}

function renderCell(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-[var(--color-text-subtle)]">NULL</span>;
  }
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "string") {
    if (value.length > 80) return value.slice(0, 80) + "…";
    return value;
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
